/**
 * Zespół: konta, z których bierze się lista osób na tablicy zadań.
 *
 * Ekran istnieje z jednego powodu: dopóki konta zakładało się wyłącznie
 * poleceniem `flask create-user`, lista osób na zadaniu miała jedną pozycję
 * i wybór z niej niczego nie zmieniał. Narzędzie do pracy zespołowej, które
 * do dodania człowieka wymaga dostępu do serwera, jest narzędziem dla jednej
 * osoby.
 *
 * Hasło startowe pokazujemy dokładnie raz, tak samo jak klucz API. Nie ma go
 * gdzie odczytać później i tak ma zostać — hasło, które da się podejrzeć
 * w panelu, jest hasłem wspólnym.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Avatar, Card, Chip, ErrorState, Loading } from "../components/ui";
import { useSession } from "../hooks/useSession";
import { api } from "../lib/api";
import { ago, LABELS } from "../lib/format";
import type { Member } from "../lib/types";

const ROLES = ["owner", "analyst", "viewer"];

const ROLE_HINT: Record<string, string> = {
  owner: "wszystko, razem z kontami zespołu",
  analyst: "zmiany w portfolio i w zadaniach, bez kont",
  viewer: "tylko podgląd",
};

export function TeamPage() {
  const queryClient = useQueryClient();
  const session = useSession();
  const isOwner = session.data?.role === "owner";

  const [form, setForm] = useState({ email: "", full_name: "", role: "analyst" });
  const [issued, setIssued] = useState<{ name: string; password: string } | null>(null);

  const people = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<{ users: Member[] }>("/users"),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["users"] });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ user: Member; password: string }>("/users", {
        email: form.email.trim(),
        full_name: form.full_name.trim() || undefined,
        role: form.role,
      }),
    onSuccess: (data) => {
      setIssued({ name: data.user.name, password: data.password });
      setForm({ email: "", full_name: "", role: "analyst" });
      refresh();
    },
  });

  const change = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch<{ user: Member; password?: string }>(`/users/${id}`, patch),
    onSuccess: (data) => {
      if (data.password) setIssued({ name: data.user.name, password: data.password });
      refresh();
    },
  });

  const rows = people.data?.users ?? [];

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Zespół</h1>
          <p className="page-head__sub">
            Konta w HQ. To z tej listy wybiera się osobę odpowiedzialną za zadanie.
          </p>
        </div>
      </header>

      <div className="stack">
        <Card title="Osoby" note="Wyłączone konto traci dostęp, ale zostaje przy swoich zadaniach">
          {people.isPending ? (
            <Loading label="Wczytuję zespół…" />
          ) : people.isError ? (
            <ErrorState error={people.error} />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Osoba</th>
                    <th>Rola</th>
                    <th>Ostatnie logowanie</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((person) => (
                    <tr key={person.id} className={person.is_active ? undefined : "row--off"}>
                      <th scope="row">
                        <span className="person">
                          <Avatar name={person.name} />
                          <span>
                            <span className="person__name">{person.name}</span>
                            {/* Konto bez imienia podpisuje się adresem — druga
                                linijka powtarzałaby wtedy to samo. */}
                            {person.full_name && (
                              <span className="person__mail dim">{person.email}</span>
                            )}
                          </span>
                        </span>
                      </th>
                      <td>
                        {isOwner ? (
                          <select
                            aria-label={`Rola: ${person.name}`}
                            value={person.role}
                            disabled={change.isPending}
                            onChange={(event) =>
                              change.mutate({ id: person.id, patch: { role: event.target.value } })
                            }
                          >
                            {ROLES.map((role) => (
                              <option key={role} value={role}>
                                {LABELS.role[role] ?? role}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Chip>{LABELS.role[person.role] ?? person.role}</Chip>
                        )}
                      </td>
                      <td className="mono">{ago(person.last_login_at)}</td>
                      <td className="team__actions">
                        {!person.is_active && <Chip tone="quiet">konto wyłączone</Chip>}
                        {isOwner && person.id !== session.data?.id && (
                          <button
                            type="button"
                            className="btn btn--quiet"
                            disabled={change.isPending}
                            onClick={() =>
                              change.mutate({
                                id: person.id,
                                patch: { is_active: !person.is_active },
                              })
                            }
                          >
                            {person.is_active ? "Wyłącz" : "Włącz"}
                          </button>
                        )}
                        {isOwner && (
                          <button
                            type="button"
                            className="btn btn--quiet"
                            disabled={change.isPending}
                            onClick={() =>
                              change.mutate({ id: person.id, patch: { reset_password: true } })
                            }
                          >
                            Nowe hasło
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {change.isError && (
            <p className="form-error" role="alert">
              {change.error instanceof Error ? change.error.message : "Nie udało się zapisać"}
            </p>
          )}
        </Card>

        {issued && (
          <div className="secret" role="status">
            <p className="secret__warning">
              Hasło dla {issued.name} pokazujemy raz. Przekaż je tej osobie kanałem, w którym
              ufasz drugiej stronie — po zamknięciu nie da się go odczytać.
            </p>
            <dl>
              <dt>HASŁO STARTOWE</dt>
              <dd className="mono">{issued.password}</dd>
            </dl>
            <button type="button" className="btn btn--quiet" onClick={() => setIssued(null)}>
              Przekazałem, ukryj
            </button>
          </div>
        )}

        {isOwner ? (
          <Card title="Nowa osoba" note="Konto powstaje od razu, razem z hasłem startowym">
            <form
              className="form-grid"
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate();
              }}
            >
              <label>
                <span>Adres e-mail</span>
                <input
                  required
                  type="email"
                  placeholder="anna@folwarklabs.pl"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
                <small>Na ten adres pójdą powiadomienia o przypisanych zadaniach.</small>
              </label>
              <label>
                <span>Imię i nazwisko</span>
                <input
                  placeholder="Anna Kowalczyk"
                  value={form.full_name}
                  onChange={(event) => setForm({ ...form, full_name: event.target.value })}
                />
                <small>Ten podpis zobaczysz na kartach zadań.</small>
              </label>
              <label>
                <span>Rola</span>
                <select
                  value={form.role}
                  onChange={(event) => setForm({ ...form, role: event.target.value })}
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {LABELS.role[role] ?? role}
                    </option>
                  ))}
                </select>
                <small>{ROLE_HINT[form.role]}</small>
              </label>

              {create.isError && (
                <p className="form-error form-grid__wide" role="alert">
                  {create.error instanceof Error ? create.error.message : "Nie udało się dodać"}
                </p>
              )}

              <div className="form-grid__wide">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={create.isPending || !form.email.trim()}
                >
                  {create.isPending ? "Zakładam…" : "Załóż konto"}
                </button>
              </div>
            </form>
          </Card>
        ) : (
          <p className="card__note">
            Konta zakłada i wyłącza właściciel. Listę widzisz, bo bez niej nie dałoby się wskazać
            osoby odpowiedzialnej za zadanie.
          </p>
        )}
      </div>
    </>
  );
}
